import React, { useEffect, useState, useRef } from "react";
import axiosInstance from "../../../../../httpInterceptor";
import { toast } from "react-toastify";
import { ReactMultiEmail, isEmail } from "react-multi-email";
import "react-multi-email/dist/style.css";
import { Button, IconButton } from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import CloseIcon from "@material-ui/icons/Close";
import SaveIcon from "@material-ui/icons/Save";
import { makeStyles } from "@material-ui/core/styles";
import ScaleLoader from "react-spinners/ScaleLoader";

const SendNotification = ({
  projectid,
  setName,
}: {
  projectid?: string;
  setName?: string;
}) => {
  // Default emails to be included in every email set
  const defaultEmails = [
    "Alpana.Chaturvedi@ltm.com",
    "Anita.Tripathi@ltm.com",
    "Arin.Bhattacharya@ltm.com",
    "Ravi.Muthurajan@ltm.com",
  ];

  const [emails, setEmails] = useState<string[]>([]); // Keep for backward compatibility
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [assessmentData, setAssessmentData] = useState<any>(null);
  const [findingsData, setFindingsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [progressBar, setProgressBar] = useState(false);
  const [toEmailSuggestions, setToEmailSuggestions] = useState([]);
  const [ccEmailSuggestions, setCcEmailSuggestions] = useState([]);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const [showCcSuggestions, setShowCcSuggestions] = useState(false);
  const [currentToInput, setCurrentToInput] = useState("");
  const [currentCcInput, setCurrentCcInput] = useState("");
  const [toInputKey, setToInputKey] = useState(0);
  const [ccInputKey, setCcInputKey] = useState(0);
  const [pmDmEmails, setPmDmEmails] = useState<string[]>([]);
  const [pmDmEmailRoles, setPmDmEmailRoles] = useState<{[email: string]: string[]}>({});
  const [excludedDefaultEmails, setExcludedDefaultEmails] = useState<string[]>([]);
  const [excludedPmDmEmails, setExcludedPmDmEmails] = useState<string[]>([]);
  const [pphEmails, setPphEmails] = useState<string[]>([]);
  const [pphEmailRoles, setPphEmailRoles] = useState<{[email: string]: string[]}>({});
  const [pphUploadInProgress, setPphUploadInProgress] = useState(false);
  const pphFileInputRef = useRef<HTMLInputElement | null>(null);
  const [initialPphRowCount, setInitialPphRowCount] = useState<number | null>(null);

  // Poll PPH account service after an upload to detect when the
  // background S3/Lambda processing has updated the table.
  // Optionally takes the row count before upload so we can
  // show how many rows were added/removed.
  const pollPphAccountsUpdate = (uploadStartedAtIso: string, previousCount?: number | null) => {
    const baseUrl = process.env.REACT_APP_PPH_ACCOUNT_SERVICE_BASE_URL;
    if (!baseUrl) {
      console.error("PPH Account service URL is not configured for polling.");
      toast.error("PPH Account service URL is not configured.");
      return;
    }

    const uploadStartedAt = new Date(uploadStartedAtIso).getTime();
    const POLL_INTERVAL_MS = 5000; // 5 seconds
    const POLL_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    const checkOnce = async () => {
      try {
        const res = await axiosInstance.get(`${baseUrl}/getpphs`);
        const items = res.data?.data || [];
        const currentCount: number | null =
          typeof res.data?.count === "number" ? res.data.count : Array.isArray(items) ? items.length : null;

        if (Array.isArray(items) && items.length > 0) {
          const times = items
            .map((item: any) => item.updatedAt)
            .filter((t: any) => typeof t === "string" && t.trim() !== "");

          if (times.length > 0) {
            const latest = Math.max(
              ...times.map((t: string) => new Date(t).getTime() || 0)
            );

            if (!Number.isNaN(latest) && latest >= uploadStartedAt) {
              if (typeof previousCount === "number" && typeof currentCount === "number") {
                const diff = currentCount - previousCount;
                if (diff > 0) {
                  toast.success(
                    `PPH account table updated successfully (${diff} row${diff === 1 ? "" : "s"} added).`
                  );
                } else if (diff < 0) {
                  const absDiff = Math.abs(diff);
                  toast.success(
                    `PPH account table updated successfully (${absDiff} row${absDiff === 1 ? "" : "s"} removed).`
                  );
                } else {
                  toast.success(
                    "PPH account table processed successfully (no change in row count)."
                  );
                }
              } else {
                toast.success("PPH account table updated successfully.");
              }
              return;
            }
          }
        }

        if (Date.now() < deadline) {
          setTimeout(checkOnce, POLL_INTERVAL_MS);
        } else {
          toast.warn(
            "PPH account file is still being processed. Please check again later."
          );
        }
      } catch (err) {
        console.error("Error polling PPH account status:", err);
        if (Date.now() < deadline) {
          setTimeout(checkOnce, POLL_INTERVAL_MS);
        } else {
          toast.error(
            "Could not confirm PPH account table update. Please try again later."
          );
        }
      }
    };

    checkOnce();
  };

  const handlePPHUploadClick = () => {
    if (pphFileInputRef.current) {
      pphFileInputRef.current.click();
    }
  };

  const handlePPHFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const baseUrl = process.env.REACT_APP_PPH_ACCOUNT_SERVICE_BASE_URL;
    if (!baseUrl) {
      toast.error("PPH Account service URL is not configured.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    // Fetch current row count before starting the upload so we can
    // later show how many rows were added/removed once processing finishes.
    let previousCount: number | null = null;
    try {
      const countRes = await axiosInstance.get(`${baseUrl}/getpphs`);
      if (typeof countRes.data?.count === "number") {
        previousCount = countRes.data.count;
        setInitialPphRowCount(countRes.data.count);
      }
    } catch (countError) {
      console.warn("Unable to fetch initial PPH account row count before upload:", countError);
    }

    setPphUploadInProgress(true);
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const processed = response.data?.summary?.processed;

      if (typeof processed === "number") {
        // Backend has already processed the file and returned how many rows changed
        toast.success(
          `PPH account details processed successfully (${processed} row${processed === 1 ? "" : "s"} updated).`
        );
      } else if (response.status === 202) {
        // File is only uploaded to S3; another Lambda will process it asynchronously
        toast.success(
          "PPH account file uploaded successfully. It is now being processed and the database will be updated shortly."
        );

        // Start polling the PPH account service to detect when
        // the background processor has updated the table.
        pollPphAccountsUpdate(new Date().toISOString(), previousCount);
      } else {
        // Generic success fallback
        toast.success("PPH account file uploaded successfully.");
      }
    } catch (error) {
      console.error("Error uploading PPH account details file:", error);
      toast.error("Failed to upload PPH account details. Please try again.");
    } finally {
      setPphUploadInProgress(false);
      // Reset the input so the same file can be selected again if needed
      event.target.value = "";
    }
  };

  // Function to fetch PPH emails from pph-account-service
  const fetchPPHEmails = async (duName: string, customerGroupId: string) => {
    try {
      console.log(`Fetching PPH emails for DU: ${duName}, Customer Group: ${customerGroupId}`);
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_PPH_ACCOUNT_SERVICE_BASE_URL}/search/${duName}/${customerGroupId}`
      );
      
      console.log("PPH Account Service response:", response.data);
      
      if (response.data.success && response.data.data.length > 0) {
        const pphData = response.data.data[0];
        const pphEmails = [];
        const emailRoles: {[email: string]: string[]} = {};
        
        // Add PPH_PS_Email if it exists and is not null/empty
        if (pphData.PPH_PS_Email && pphData.PPH_PS_Email.trim() !== '') {
          pphEmails.push(pphData.PPH_PS_Email);
          emailRoles[pphData.PPH_PS_Email] = ['PPH'];
        }
        
        // Add PPH_2nd_Line_PS_Email if it exists and is not null/empty
        if (pphData.PPH_2nd_Line_PS_Email && pphData.PPH_2nd_Line_PS_Email.trim() !== '') {
          if (!pphEmails.includes(pphData.PPH_2nd_Line_PS_Email)) {
            pphEmails.push(pphData.PPH_2nd_Line_PS_Email);
          }
          emailRoles[pphData.PPH_2nd_Line_PS_Email] = emailRoles[pphData.PPH_2nd_Line_PS_Email] 
            ? [...emailRoles[pphData.PPH_2nd_Line_PS_Email], 'PPH-2nd level'] 
            : ['PPH-2nd level'];
        }
        
        console.log("PPH emails extracted:", pphEmails);
        console.log("PPH email roles:", emailRoles);
        setPphEmails(pphEmails);
        setPphEmailRoles(emailRoles);
        return pphEmails;
      }
      
      return [];
    } catch (error) {
      console.error("Error fetching PPH emails:", error);
      return [];
    }
  };

  // Function to fetch email by PS number
  const fetchEmailByPSNumber = async (psNumber: string) => {
    try {
      console.log(`Fetching email for PS number: ${psNumber}`);
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_USER_SERVICE_BASE_URL}/getPSNumber/?search=${psNumber}`
      );
      console.log(`Email data for PS ${psNumber}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching email for PS number ${psNumber}:`, error);
      return [];
    }
  };

  // Function to fetch PM and DM emails
  const fetchPMAndDMEmails = async (): Promise<{ pmDmEmails: string[]; pphEmailsList: string[] }> => {
    if (projectid && setName) {
      try {
        console.log("Fetching project details for PM and DM emails from dashboard service...");
        const projectRes = await axiosInstance.get(
          `${process.env.REACT_APP_DASHBOARD_SERVICE_BASE_URL}/project/${projectid}/${setName}`
        );

        const projectInfo = projectRes.data;
        console.log("Project info from dashboard service:", projectInfo);

        const dmPsNoList = Array.isArray(projectInfo?.DM_psno)
          ? projectInfo.DM_psno
          : projectInfo?.DM_psno
          ? [projectInfo.DM_psno]
          : [];

        const pmPsNoList = Array.isArray(projectInfo?.primaryPM_psno)
          ? projectInfo.primaryPM_psno
          : projectInfo?.primaryPM_psno
          ? [projectInfo.primaryPM_psno]
          : [];

        const duname = projectInfo?.duname || "N/A";
        const customergroupid = projectInfo?.accountId || "N/A";

        console.log("DM PS Numbers:", dmPsNoList);
        console.log("PM PS Numbers:", pmPsNoList);
        console.log("DU Name:", duname);
        console.log("Customer Group ID:", customergroupid);

        const pmDmEmails: string[] = [];
        const emailRoles: { [email: string]: string[] } = {};

        // Fetch PPH emails if we have DU and customer group id
        let pphEmailsList: string[] = [];
        if (duname && customergroupid && duname !== "N/A" && customergroupid !== "N/A") {
          console.log("Fetching PPH emails...");
          pphEmailsList = await fetchPPHEmails(duname, customergroupid);
        }

        // Fetch DM emails for each PS number
        if (dmPsNoList.length > 0) {
          for (const dmPsNo of dmPsNoList) {
            console.log(`Fetching DM email for PS Number: ${dmPsNo}`);
            const dmEmailData = await fetchEmailByPSNumber(dmPsNo);
            console.log("DM Email response:", dmEmailData);
            if (dmEmailData.length > 0 && dmEmailData[0]?.email) {
              const dmEmail = dmEmailData[0].email;
              if (!pmDmEmails.includes(dmEmail)) {
                pmDmEmails.push(dmEmail);
              }
              emailRoles[dmEmail] = emailRoles[dmEmail]
                ? [...emailRoles[dmEmail], "DM"]
                : ["DM"];
              console.log("DM Email added:", dmEmail);
            } else {
              console.log("No DM email found or invalid response for PS:", dmPsNo);
            }
          }
        } else {
          console.log("DM PS Numbers not found in project data");
        }

        // Fetch PM emails for each PS number
        if (pmPsNoList.length > 0) {
          for (const pmPsNo of pmPsNoList) {
            console.log(`Fetching PM email for PS Number: ${pmPsNo}`);
            const pmEmailData = await fetchEmailByPSNumber(pmPsNo);
            console.log("PM Email response:", pmEmailData);
            if (pmEmailData.length > 0 && pmEmailData[0]?.email) {
              const pmEmail = pmEmailData[0].email;
              if (!pmDmEmails.includes(pmEmail)) {
                pmDmEmails.push(pmEmail);
              }
              emailRoles[pmEmail] = emailRoles[pmEmail]
                ? [...emailRoles[pmEmail], "PM"]
                : ["PM"];
              console.log("PM Email added:", pmEmail);
            } else {
              console.log("No PM email found or invalid response for PS:", pmPsNo);
            }
          }
        } else {
          console.log("PM PS Numbers not found in project data");
        }

        console.log("All PM/DM emails:", pmDmEmails);
        console.log("Email roles mapping:", emailRoles);
        setPmDmEmails(pmDmEmails);
        setPmDmEmailRoles(emailRoles);

        // Separate PM/DM and PPH lists
        console.log("PPH emails list:", pphEmailsList);

        return { pmDmEmails, pphEmailsList };
      } catch (error) {
        console.error("Error fetching PM and DM emails:", error);
        return { pmDmEmails: [], pphEmailsList: [] };
      }
    }
    return { pmDmEmails: [], pphEmailsList: [] };
  };

  useEffect(() => {
    console.log("useEffect triggered with projectid:", projectid, "setName:", setName);
    console.log("Environment variables:");
    console.log("REACT_APP_USER_SERVICE_BASE_URL:", process.env.REACT_APP_USER_SERVICE_BASE_URL);
    console.log("REACT_APP_COMPASS_BOOMI_URL:", process.env.REACT_APP_COMPASS_BOOMI_URL);
    console.log("REACT_APP_COMPASS_X_API_KEY:", process.env.REACT_APP_COMPASS_X_API_KEY ? "SET" : "NOT SET");
    console.log("REACT_APP_ASSESSMENT_SERVICE_BASE_URL:", process.env.REACT_APP_ASSESSMENT_SERVICE_BASE_URL);
    
    const fetchAssessmentData = async () => {
      if (projectid && setName) {
        console.log("Starting to fetch assessment data and PM/DM emails...");
        try {
          const response = await axiosInstance.get(
            `${process.env.REACT_APP_ASSESSMENT_SERVICE_BASE_URL}/get-projectassesment/${projectid}/${setName}`
          );
          setAssessmentData(response.data);
          console.log("Assessment data response:", response);
          
          // Get existing emails from assessment data
          const existingEmails = response.data[0]?.emailIds || [];
          console.log("Existing emails from assessment data:", existingEmails);
          
          // Fetch PM/DM and PPH emails
          const { pmDmEmails, pphEmailsList } = await fetchPMAndDMEmails();
          console.log("PM/DM emails fetched:", pmDmEmails);
          console.log("PPH emails fetched:", pphEmailsList);
          
          // Filter out excluded emails
          const activeDefaultEmails = defaultEmails.filter(email => !excludedDefaultEmails.includes(email));
          const activePmDmEmails = pmDmEmails.filter((email: string) => !excludedPmDmEmails.includes(email));
          const activePphEmails = pphEmailsList.filter((email: string) => !excludedPmDmEmails.includes(email));
          
          // TO field: only PPH 1st line and PPH 2nd line
          const toEmailsList = [...activePphEmails];
          const uniqueToEmails = toEmailsList.filter((email, index) => toEmailsList.indexOf(email) === index);
          
          // CC field: PM/DM emails + default emails
          const ccEmailsList = [...activePmDmEmails, ...activeDefaultEmails];
          const uniqueCcEmails = ccEmailsList.filter((email, index) => ccEmailsList.indexOf(email) === index);
          
          setToEmails(uniqueToEmails);
          setCcEmails(uniqueCcEmails);
          
          // Keep backward compatibility
          const allEmails = [...uniqueToEmails, ...uniqueCcEmails];
          setEmails(allEmails);
          
          console.log("TO Emails (PM/DM/PPH):", uniqueToEmails);
          console.log("CC Emails (Default):", uniqueCcEmails);
        } catch (err) {
          console.error("Error fetching assessment data:", err);
          setError("Failed to load assessment data");
        } finally {
          setLoading(false);
        }
      } else {
        console.log("Missing projectid or setName, not fetching data");
        setLoading(false);
      }
    };

    fetchAssessmentData();
  }, [projectid, setName]);

  // Hide suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.reactMultiEmail') && !target.closest('[data-suggestion-dropdown]')) {
        setShowToSuggestions(false);
        setShowCcSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchEmailSuggestions = async (search: string, type: 'to' | 'cc') => {
    if (!search || search.trim() === '') {
      if (type === 'to') {
        setToEmailSuggestions([]);
        setShowToSuggestions(false);
      } else {
        setCcEmailSuggestions([]);
        setShowCcSuggestions(false);
      }
      return;
    }

    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_USER_SERVICE_BASE_URL}/getPSNumber/?search=${search}`
      );
      if (type === 'to') {
        setToEmailSuggestions(response.data);
        setShowToSuggestions(true);
      } else {
        setCcEmailSuggestions(response.data);
        setShowCcSuggestions(true);
      }
      console.log(`${type.toUpperCase()} Email suggestion list`, response.data);
    } catch (error) {
      console.error(`Error fetching ${type} email suggestions:`, error);
      if (type === 'to') {
        setToEmailSuggestions([]);
        setShowToSuggestions(true);
      } else {
        setCcEmailSuggestions([]);
        setShowCcSuggestions(true);
      }
    }
  };

  const handleSaveEmails = async () => {
    try {
      // Combine TO and CC emails for storage
      const emailsToSave = [...toEmails, ...ccEmails];
      console.log("TO Emails to save:", toEmails);
      console.log("CC Emails to save:", ccEmails);
      console.log("All emails to save:", emailsToSave);
      
      const projectKey = `${projectid}_${setName}`;
      const storedData = JSON.parse(localStorage.getItem("emailData") || "{}");
      
      // Store TO and CC separately
      storedData[projectKey] = {
        toEmails: toEmails,
        ccEmails: ccEmails,
        allEmails: emailsToSave // For backward compatibility
      };
      localStorage.setItem("emailData", JSON.stringify(storedData));
      
      console.log("Updated email IDs:", emailsToSave);

      // Send email IDs to the backend
      if (projectid && setName) {
        try {
          const response = await axiosInstance.post(
            `${process.env.REACT_APP_ASSESSMENT_SERVICE_BASE_URL}/create-projectassesment`,
            {
              projectid: projectid,
              set_name: setName,
              emailIds: emailsToSave,
              toEmails: toEmails,
              ccEmails: ccEmails
            }
          );
          console.log("Response from API:", response);
          if (response.data) {
            toast.success("Email IDs updated successfully");
          }
        } catch (error) {
          console.error("Error updating email IDs:", error);
          toast.error("Failed to update email IDs");
        }
      }
    } catch (error) {
      console.error("Error in handleSaveEmails:", error);
      toast.error("Failed to save emails");
    }
  };

  const handleSendEmail = async () => {
    await handleSaveEmails();

    if (!assessmentData || (toEmails.length === 0 && ccEmails.length === 0)) {
      toast.error(
        "Please ensure assessment data is loaded and at least one email is entered in TO or CC field."
      );
      return;
    }

    // Validate TO emails
    const invalidToEmails = toEmails.filter((email) => !isEmail(email));
    if (invalidToEmails.length > 0) {
      toast.error(`Invalid TO email(s): ${invalidToEmails.join(", ")}`);
      return;
    }
    
    // Validate CC emails
    const invalidCcEmails = ccEmails.filter((email) => !isEmail(email));
    if (invalidCcEmails.length > 0) {
      toast.error(`Invalid CC email(s): ${invalidCcEmails.join(", ")}`);
      return;
    }
    
    setProgressBar(true);

    try {
      const [projectRes, assessmentSummaryRes] = await Promise.all([
        axiosInstance.get(
          `${process.env.REACT_APP_DASHBOARD_SERVICE_BASE_URL}/project/${projectid}/${setName}`
        ),
        axiosInstance.get(
          `${process.env.REACT_APP_ASSESSMENT_SERVICE_BASE_URL}/latest-assessment/${projectid?.trim()}/${setName}`
        ),
      ]);

      const projectData = projectRes.data;
      const projectSummaryData = {
        projectDetails: projectData,
        puBuAccount: projectData,
        assessmentSummary: assessmentSummaryRes.data,
      };

      // Fetch latest PDF URL from report API
      const reportRes = await axiosInstance.get(
        `${process.env.REACT_APP_LLM_SERVICE_BASE_URL}/report/${projectid?.trim()}/${setName}`
      );
      const pdfUrl = reportRes.data?.latest_url || "";

      // Fetch case study data
      let caseStudyData = [];
      try {
        console.log("Fetching case study data for email...");
        
        // Step 1: Fetch project details
        const projectDetailsRes = await axiosInstance.get(
          `${process.env.REACT_APP_DASHBOARD_SERVICE_BASE_URL}/project-details/${projectid}/${setName}`
        );
        
        const rawProjectDetails = projectDetailsRes.data || {};
        
        // Debug: Log all available fields in project details
        console.log("Raw Project Details Keys:", Object.keys(rawProjectDetails));
        console.log("PU related fields:", {
          pu: rawProjectDetails.pu,
          u_practice_unit: rawProjectDetails.u_practice_unit,
          PU: rawProjectDetails.PU,
          du: rawProjectDetails.du,
          delivery_unit: rawProjectDetails.delivery_unit
        });
        
        // Fallback DU-to-Industry map
        const DU_TO_INDUSTRY: Record<string, string> = {
          BB: "BANKING & FINANCIAL SERVICES",
          CSU: "BANKING & FINANCIAL SERVICES",
          DSA: "BANKING & FINANCIAL SERVICES",
          BC: "BANKING & FINANCIAL SERVICES",
          ND: "BANKING & FINANCIAL SERVICES",
          BF: "BANKING & FINANCIAL SERVICES",
          EG: "ENERGY",
          GP: "ENERGY",
          EY: "ENERGY",
          HA: "HEALTH",
          HE: "HEALTH",
          HT: "HI-TECH",
          HH: "HI-TECH",
          NN: "INSURANCE",
          DL: "INSURANCE",
          NC: "INSURANCE",
          NS: "INSURANCE",
          PN: "INSURANCE",
          LS: "LIFE SCIENCES",
          MM: "MANUFACTURING",
          MN: "MANUFACTURING",
          ME: "MEDIA & ENTERTAINMENT",
          MD: "MEDIA & ENTERTAINMENT",
          FT: "MICROSOFT",
          RR: "RETAIL & CONSUMER PACKAGED GOODS",
          RE: "RETAIL & CONSUMER PACKAGED GOODS",
          TW: "TRAVEL TRANSPORT & HOSPITALITY",
          DATA: "TRAVEL TRANSPORT & HOSPITALITY",
          UL: "UTILITIES",
          UA: "UTILITIES"
        };

        const duValue = (
          rawProjectDetails.du || rawProjectDetails.delivery_unit || 
          rawProjectDetails.u_delivery_unit || rawProjectDetails.DU || ''
        ).toString().trim().toUpperCase();

        const industryCandidates = [
          rawProjectDetails.mapped_industry,
          rawProjectDetails.industry,
          rawProjectDetails.mappedIndustry,
        ].filter(v => typeof v === 'string' && v.trim() !== '');

        let mapped_industry = industryCandidates.length > 0 ? industryCandidates[0].toString().trim() : '';
        
        if (!mapped_industry || /^N\/?A$/i.test(mapped_industry)) {
          mapped_industry = DU_TO_INDUSTRY[duValue] || 'N/A';
        }

        const sanitizeArray = (val: any): string[] => {
          if (!val) return [];
          if (Array.isArray(val)) return val.filter(v => !!v && v !== 'N/A' && v !== 'NA');
          if (typeof val === 'string') {
            const trimmed = val.trim();
            if (trimmed === '' || trimmed.toUpperCase() === 'N/A' || trimmed.toUpperCase() === 'NA') return [];
            return [trimmed];
          }
          return [];
        };

        const isNAValue = (value: string | string[]): boolean => {
          if (Array.isArray(value)) {
            return value.length === 0 || value.some(v => 
              v && typeof v === 'string' && 
              (v.toUpperCase() === "N/A" || v.toUpperCase() === "NA")
            );
          }
          return !value || (typeof value === 'string' && 
            (value.toUpperCase() === "N/A" || value.toUpperCase() === "NA"));
        };

        const capability_area = sanitizeArray(rawProjectDetails.capability_area);
        const capability = sanitizeArray(rawProjectDetails.capability);
        const account = rawProjectDetails.account || rawProjectDetails.account_name || '';

        // Helper function to map PU to practice_unit_id
        const getPracticeUnitId = (puValue: string | undefined): number => {
          if (!puValue) return 2; // Default to Quality Engineering (TT)
          const normalized = puValue.toUpperCase().trim();
          return normalized === 'DL' ? 1 : 2; // DL = Digital Engineering (1), TT = Quality Engineering (2)
        };

        // Get PU value from project details (prioritize PU field, NOT DU)
        const puValue = (
          rawProjectDetails.pu || rawProjectDetails.u_practice_unit || 
          rawProjectDetails.PU || ''
        ).toString().trim().toUpperCase();

        console.log(" PU Value extracted:", puValue);
        console.log(" PU Value is empty?", !puValue);

        const practiceUnitId = getPracticeUnitId(puValue);

        console.log("Practice Unit ID determined:", practiceUnitId);

        // Step 2: Fetch case studies with fallback mechanism
        const caseStudyPayload = {
          capabilityarea: isNAValue(capability_area) ? [] : capability_area,
          capabilities: isNAValue(capability) ? [] : capability,
          industry: isNAValue(mapped_industry) ? [] : [mapped_industry],
          account: isNAValue(account) ? [] : [account],
          casestudyfilename: [],
          endmonth: null,
          endyear: null,
          geography: [],
          startmonth: null,
          startyear: null,
          teamsize: "",
          teamsizeoperator: "",
          username: sessionStorage.getItem("PBT_EMAIL") || "",
          practice_unit_id: practiceUnitId // Add practice_unit_id based on PU
        };

        console.log("Fetching case studies with primary filters:");
        console.log("PU Value:", puValue);
        console.log("Practice Unit ID:", practiceUnitId);
        console.log("Case study payload for email:", caseStudyPayload);

        let caseStudyRes = await axiosInstance.post(
          `${process.env.REACT_APP_CASE_STUDY_SERVICE_BASE_URL}/casestudy/filters`,
          caseStudyPayload,
          {
            headers: {
              "X-API-KEY": "PBTteamkey",
              "Content-Type": "application/json"
            }
          }
        );

        console.log("========== RAW CASE STUDY API RESPONSE (Primary) ==========");
        console.log("Response data type:", typeof caseStudyRes.data);
        console.log("Response data is array:", Array.isArray(caseStudyRes.data));
        console.log("Response data length:", caseStudyRes.data?.length);
        console.log("First 3 items:", JSON.stringify(caseStudyRes.data?.slice(0, 3), null, 2));
        console.log("==========================================================");

        // If no results with primary practice_unit_id, try the alternate one
        if (Array.isArray(caseStudyRes.data) && caseStudyRes.data.length === 0) {
          const alternatePracticeUnitId = practiceUnitId === 1 ? 2 : 1;
          console.log("No case studies found with primary practice_unit_id:", practiceUnitId);
          console.log("Trying alternate practice_unit_id:", alternatePracticeUnitId);
          
          const alternateCaseStudyPayload = {
            ...caseStudyPayload,
            practice_unit_id: alternatePracticeUnitId
          };

          caseStudyRes = await axiosInstance.post(
            `${process.env.REACT_APP_CASE_STUDY_SERVICE_BASE_URL}/casestudy/filters`,
            alternateCaseStudyPayload,
            {
              headers: {
                "X-API-KEY": "PBTteamkey",
                "Content-Type": "application/json"
              }
            }
          );

          console.log("========== RAW CASE STUDY API RESPONSE (Alternate) ==========");
          console.log("Response data type:", typeof caseStudyRes.data);
          console.log("Response data is array:", Array.isArray(caseStudyRes.data));
          console.log("Response data length:", caseStudyRes.data?.length);
          console.log("First 3 items:", JSON.stringify(caseStudyRes.data?.slice(0, 3), null, 2));
          console.log("===========================================================");
        }

        // Process case study response - extract top 5
        if (Array.isArray(caseStudyRes.data)) {
          console.log("Processing case study data...");
          const processedCaseStudies = caseStudyRes.data
            .map((item, index) => {
              console.log(`Item ${index}:`, JSON.stringify(item));
              const key = Object.keys(item)[0];
              console.log(`  Key at index 0:`, key);
              if (!key) {
                console.log(`  No key found, returning null`);
                return null;
              }
              const value = item[key];
              console.log(`  Value for key "${key}":`, JSON.stringify(value));
              return value;
            })
            .filter(Boolean)
            .slice(0, 3);
          
          caseStudyData = processedCaseStudies;
          console.log("Processed case study data for email:", caseStudyData);
          console.log("Processed case study count:", caseStudyData.length);
        } else {
          console.log("Case study response is not an array, type:", typeof caseStudyRes.data);
        }
      } catch (caseStudyError) {
        console.error("Error fetching case study data for email:", caseStudyError);
        // Continue without case studies - don't fail the whole email
      }

      console.log("📧 Sending email request with case study data:");
      console.log("Number of case studies being sent:", caseStudyData.length);
      console.log("Case study names:", caseStudyData.map(cs => cs.case_study_name));
      console.log("Full case study data being sent:", JSON.stringify(caseStudyData, null, 2));

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_EMAIL_SERVICE_BASE_URL}/email`,
        {
          projectid: projectid,
          setName: setName,
          toEmail: toEmails,
          ccEmail: ccEmails,
          emailType: "project_and_findings",
          projectData: projectSummaryData,
          pdfUrl: pdfUrl,
          caseStudyData: caseStudyData,
        }
      );

      if (response.data) {
        toast.success("Email sent successfully!");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setProgressBar(false);
    }
  };

  const handleRemoveToEmail = async (index: number) => {
    const emailToRemove = toEmails[index];
    
    try {
      // Check if it's a PM/DM/PPH email
      if (pmDmEmails.includes(emailToRemove) || pphEmails.includes(emailToRemove)) {
        // Add to excluded PM/DM emails list
        setExcludedPmDmEmails(prev => [...prev, emailToRemove]);
        toast.success("PM/DM/PPH email removed from TO field");
      } else {
        // Regular email - remove from backend
        const response = await axiosInstance.delete(
          `${process.env.REACT_APP_ASSESSMENT_SERVICE_BASE_URL}/deleteEmail/${projectid}/${setName}`,
          { data: { emailId: emailToRemove } }
        );
        if (response.data.message) {
          toast.success(response.data.message);
        }
      }
      
      // Remove from TO emails list
      const filteredToEmails = toEmails.filter((_, i) => i !== index);
      setToEmails(filteredToEmails);
      
      // Update backward compatibility
      setEmails([...filteredToEmails, ...ccEmails]);
      
    } catch (error) {
      console.error("Error deleting TO email:", error);
      toast.error("Failed to delete TO email");
    }
  };

  const handleRemoveCcEmail = async (index: number) => {
    const emailToRemove = ccEmails[index];
    
    try {
      // Check if it's a default email
      if (defaultEmails.includes(emailToRemove)) {
        // Add to excluded default emails list
        setExcludedDefaultEmails(prev => [...prev, emailToRemove]);
        toast.success("Default email removed from CC field");
      } else {
        // Regular email - remove from backend
        const response = await axiosInstance.delete(
          `${process.env.REACT_APP_ASSESSMENT_SERVICE_BASE_URL}/deleteEmail/${projectid}/${setName}`,
          { data: { emailId: emailToRemove } }
        );
        if (response.data.message) {
          toast.success(response.data.message);
        }
      }
      
      // Remove from CC emails list
      const filteredCcEmails = ccEmails.filter((_, i) => i !== index);
      setCcEmails(filteredCcEmails);
      
      // Update backward compatibility
      setEmails([...toEmails, ...filteredCcEmails]);
      
    } catch (error) {
      console.error("Error deleting CC email:", error);
      toast.error("Failed to delete CC email");
    }
  };

  const handleRemoveEmail = async (index: number) => {
    const emailToRemove = emails[index];
    
    try {
      // Check if it's a default email or PM/DM email and handle accordingly
      if (defaultEmails.includes(emailToRemove)) {
        // Add to excluded default emails list
        setExcludedDefaultEmails(prev => [...prev, emailToRemove]);
        toast.success("Default email removed from current session");
      } else if (pmDmEmails.includes(emailToRemove)) {
        // Add to excluded PM/DM emails list
        setExcludedPmDmEmails(prev => [...prev, emailToRemove]);
        toast.success("PM/DM email removed from current session");
      } else {
        // Regular email - remove from backend
        const response = await axiosInstance.delete(
          `${process.env.REACT_APP_ASSESSMENT_SERVICE_BASE_URL}/deleteEmail/${projectid}/${setName}`,
          { data: { emailId: emailToRemove } }
        );
        if (response.data.message) {
          toast.success(response.data.message);
        }
      }
      
      // Remove from current emails list
      const filteredEmails = emails.filter((_, i) => i !== index);
      setEmails(filteredEmails);
      
      // Update localStorage
      const projectKey = `${projectid}_${setName}`;
      const storedData = JSON.parse(localStorage.getItem("emailData") || "{}");
      storedData[projectKey] = filteredEmails;
      localStorage.setItem("emailData", JSON.stringify(storedData));
      
    } catch (error) {
      console.error("Error deleting email:", error);
      toast.error("Failed to delete email");
    }
  };

  const refreshPMDMEmails = async () => {
    try {
      console.log("Refreshing PM/DM/PPH emails...");
      const { pmDmEmails: refreshedPmDmEmails, pphEmailsList: refreshedPphEmails } = await fetchPMAndDMEmails();
      
      // Reset excluded PM/DM emails when refreshing
      setExcludedPmDmEmails([]);
      
      // Update TO emails with refreshed PPH emails only
      const userToEmails = toEmails.filter((email: string) => 
        !pmDmEmails.includes(email) && !pphEmails.includes(email)
      );
      const updatedToEmails = [...userToEmails, ...refreshedPphEmails];
      const uniqueToEmails = updatedToEmails.filter((email, index) => 
        updatedToEmails.indexOf(email) === index
      );
      
      setToEmails(uniqueToEmails);
      
      // Update backward compatibility
      setEmails([...uniqueToEmails, ...ccEmails]);
      
      toast.success("PM/DM/PPH emails refreshed successfully");
      console.log("Refreshed TO emails:", uniqueToEmails);
      console.log("Updated PM/DM email roles:", pmDmEmailRoles);
      console.log("Updated PPH email roles:", pphEmailRoles);
    } catch (error) {
      console.error("Error refreshing PM/DM emails:", error);
      toast.error("Failed to refresh PM/DM emails");
    }
  };

  const restoreDefaultEmails = () => {
    try {
      // Restore all default emails to CC
      setExcludedDefaultEmails([]);
      
      // Add back all default emails that aren't already in CC
      const activeDefaultEmails = defaultEmails.filter(email => !ccEmails.includes(email));
      const updatedCcEmails = [...ccEmails, ...activeDefaultEmails];
      
      setCcEmails(updatedCcEmails);
      
      // Update backward compatibility
      setEmails([...toEmails, ...updatedCcEmails]);
      
      toast.success("Default emails restored to CC");
    } catch (error) {
      console.error("Error restoring default emails:", error);
      toast.error("Failed to restore default emails");
    }
  };

  const restorePMDMEmails = () => {
    try {
      // Restore all PM/DM/PPH emails to TO
      setExcludedPmDmEmails([]);
      
      // Combine PM/DM and PPH emails
      const allPmDmPphEmails = [...pmDmEmails, ...pphEmails];
      const uniquePmDmPphEmails = allPmDmPphEmails.filter((email, index) => 
        allPmDmPphEmails.indexOf(email) === index
      );
      
      // Add back all PM/DM/PPH emails that aren't already in TO
      const activePmDmPphEmails = uniquePmDmPphEmails.filter(email => !toEmails.includes(email));
      const updatedToEmails = [...toEmails, ...activePmDmPphEmails];
      
      setToEmails(updatedToEmails);
      
      // Update backward compatibility
      setEmails([...updatedToEmails, ...ccEmails]);
      
      toast.success("PM/DM/PPH emails restored to TO");
    } catch (error) {
      console.error("Error restoring PM/DM emails:", error);
      toast.error("Failed to restore PM/DM emails");
    }
  };

  const getEmailType = (email: string) => {
    if (defaultEmails.includes(email)) return 'default';
    if (pmDmEmails.includes(email) || pphEmails.includes(email)) return 'pm-dm-pph';
    return 'user';
  };

  const getEmailTypeColor = (email: string) => {
    const type = getEmailType(email);
    switch (type) {
      case 'default':
        return '#e3f2fd'; // Light blue for default emails
      case 'pm-dm-pph':
        const isPph = pphEmails.includes(email);
        return isPph ? '#e8f5e9' : '#fff3e0'; // Light green for PPH, light orange for PM/DM
      default:
        return '#ddd'; // Default gray for user emails
    }
  };

  const getEmailTypeLabel = (email: string) => {
    const type = getEmailType(email);
    switch (type) {
      case 'default':
        return '';
      case 'pm-dm-pph':
        // Check if this email has PPH role mapping
        if (pphEmailRoles[email]) {
          const roles = pphEmailRoles[email];
          return `(${roles.join(' & ')})`;
        }
        // Check if this email has PM/DM role mapping
        if (pmDmEmailRoles[email]) {
          const roles = pmDmEmailRoles[email];
          return `(${roles.join(' & ')})`;
        }
        return '(PM/DM/PPH)';
      default:
        return '';
    }
  };
  const useStyles = makeStyles((theme) => ({
    modalBox: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      background: "#ddd",

      boxShadow: theme.shadows[5],
      padding: theme.spacing(2, 4, 3),
      outline: "none",
      height: "40vh",
      borderRadius: "4px",
    },
    emailField: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    buttonEmail: {
      display: "flex",
      justifyContent: "space-between",
    },
    emailTag: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    label: {
      color: " #074173",
    },
  }));
  const classes = useStyles();


  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f5f5f5",
        height: "70vh",
        boxShadow:
          " rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
        position: "relative",
      }}
    >
      <style>
        {`
          .reactMultiEmail input::placeholder,
          .react-multi-email input::placeholder,
          div[class*="react-multi-email"] input::placeholder,
          .reactMultiEmail input::-webkit-input-placeholder,
          .react-multi-email input::-webkit-input-placeholder,
          div[class*="react-multi-email"] input::-webkit-input-placeholder {
            color: #074173 !important;
            opacity: 1 !important;
          }
          .reactMultiEmail input::-moz-placeholder,
          .react-multi-email input::-moz-placeholder,
          div[class*="react-multi-email"] input::-moz-placeholder {
            color: #074173 !important;
            opacity: 1 !important;
          }
          .reactMultiEmail input:-ms-input-placeholder,
          .react-multi-email input:-ms-input-placeholder,
          div[class*="react-multi-email"] input:-ms-input-placeholder {
            color: #074173 !important;
            opacity: 1 !important;
          }
          .reactMultiEmail input::-ms-input-placeholder,
          .react-multi-email input::-ms-input-placeholder,
          div[class*="react-multi-email"] input::-ms-input-placeholder {
            color: #074173 !important;
            opacity: 1 !important;
          }
        `}
      </style>
      <div
        style={{
          backgroundColor: "rgb(165, 164, 164)",
          padding: "1.3rem",
          borderRadius: "3px",
          transform: "translate(-25px, -25px)",
          width: "100.6%",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          boxSizing: "border-box",
        }}
      >
        <input
          type="file"
          accept=".xlsx,.xls"
          ref={pphFileInputRef}
          style={{ display: "none" }}
          onChange={handlePPHFileChange}
        />
        <Button
          variant="contained"
          color="secondary"
          onClick={handlePPHUploadClick}
          disabled={pphUploadInProgress}
          style={{
            backgroundColor: "#FF5E4F",
            color: "#fff",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {pphUploadInProgress
            ? "Uploading PPH-Account Mapping..."
            : "Upload PPH-Account Mapping"}
        </Button>
      </div>

      {progressBar && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            translate: "26rem -12px",
            position: "absolute",
          }}
        >
          <p style={{ color: "#FF5E4F", margin: 0, paddingRight: "10px" }}>
            Please wait the email to be sent...
          </p>
          <ScaleLoader
            style={{ translate: "-5px" }}
            color="#e73d2eff"
            height={15}
            width={2}
            margin={2}
          />
        </div>
      )}

      <div>
        <div
          style={{
            position: "relative",
            transform: "translate(200px, 20px)",
            backgroundColor: "#f5f5f5",
            width: "700px",
            height: "350px",
            boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.2)",
            border: "none",
            boxSizing: "border-box",
            borderRadius: "8px",
            padding: "16px",
          }}
        >
          <label
            htmlFor="email"
            style={{
              transform: "translate(200px, -30px)",
              fontSize: "16px",
              color: "grey",
              fontWeight: "bold",
              marginLeft: "15px",
            }}
          >
            Enter Email Addresses:
          </label>

          {/* Status Information */}
          {(excludedDefaultEmails.length > 0 || excludedPmDmEmails.length > 0) && (
            <div style={{
              position: "absolute",
              bottom: "50px",
              left: "33px",
              fontSize: "11px",
              color: "#666",
              backgroundColor: "#fff",
              padding: "4px 8px",
              borderRadius: "4px",
              border: "1px solid #ddd",
              zIndex: 10
            }}>
              {excludedDefaultEmails.length > 0 && (
                <div>Excluded Default: {excludedDefaultEmails.length}</div>
              )}
              {excludedPmDmEmails.length > 0 && (
                <div>Excluded PM/DM/PPH: {excludedPmDmEmails.length}</div>
              )}
            </div>
          )}

          <div
            style={{
              width: "700px",
              translate: "0px,-70px",
            }}
          >
            {/* TO Field */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  fontSize: "14px",
                  color: "black",
                  fontWeight: "600",
                  marginLeft: "16px",
                  display: "block",
                  marginBottom: "8px"
                }}
              >
                To: PPH
              </label>
              <ReactMultiEmail
                key={toInputKey}
                placeholder={loading ? "Email ID fetching..." : "Add TO recipients"}
                emails={toEmails}
                className="reactMultiEmail"
                style={{
                  minHeight: "30px",
                  maxHeight: "80px",
                  overflowY: "auto",
                  backgroundColor: "white",
                  scrollbarColor: "#FF5E4F white",
                  padding: "0.5rem",
                  borderRadius: "4px",
                  marginLeft: "16px",
                  width: "635px",
                  scrollbarWidth:'thin',
                  border:"1px solid #FF5E4F",
                  color: "black"
                }}
                onChange={(newEmails: string[]) => {
                  const validEmails = newEmails.filter((email) =>
                    email.toLowerCase().endsWith("@ltimindtree.com")
                  );

                  if (validEmails.length !== newEmails.length) {
                    toast.error("Please use a valid Email Address");
                  }

                  setToEmails(validEmails);
                  setEmails([...validEmails, ...ccEmails]);
                  setShowToSuggestions(false);
                }}
                onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  const target = e.target as HTMLInputElement;
                  const value = target.value.trim();
                  setCurrentToInput(value);
                  if (value === '') {
                    setShowToSuggestions(false);
                    setToEmailSuggestions([]);
                  } else {
                    fetchEmailSuggestions(value, 'to');
                  }
                }}
                onFocus={() => {
                  if (currentToInput && currentToInput.trim() !== '') {
                    fetchEmailSuggestions(currentToInput, 'to');
                  }
                }}
                getLabel={(email, index, removeEmail) => (
                  <div
                    key={index}
                    className={classes.emailTag}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: getEmailTypeColor(email),
                      border: "0px solid transparent",
                      borderRadius: "100px",
                      margin: "0.2rem",
                    }}
                  >
                    <div
                      style={{
                        marginRight: "0.5rem",
                        translate: "10px -1px",
                        color: "#3c3c3c",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start"
                      }}
                    >
                      <span>{email}</span>
                      {getEmailTypeLabel(email) && (
                        <span style={{ 
                          fontSize: "10px", 
                          color: "#666",
                          fontWeight: "bold"
                        }}>
                          {getEmailTypeLabel(email)}
                        </span>
                      )}
                    </div>
                    <IconButton
                      onClick={() => handleRemoveToEmail(index)}
                      size="small"
                    >
                      <CloseIcon
                        style={{
                          color: "grey",
                          fontSize: "1.3rem",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "50%",
                          transition: "transform 0.2s, color 0.2s",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.color = "#FF5E4F";
                          e.currentTarget.style.transform = "scale(1.2)";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.color = "grey";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      />
                    </IconButton>
                  </div>
                )}
              />
              
              {showToSuggestions && (
                <div
                  data-suggestion-dropdown="true"
                  style={{
                    position: "absolute",
                    top: "60px",
                    right: "-200px",
                    width: "300px",
                    backgroundColor: "white",
                    border: "1px solid  #FF5E4F",
                    borderRadius: "4px",
                    maxHeight: "150px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                    scrollbarWidth:'thin',
                  }}
                > 
                  {toEmailSuggestions.length > 0 ? (
                    toEmailSuggestions.map((suggestion: any, index: number) => (
                      <div
                        key={index}
                        onClick={() => {
                          const newToEmails = [...toEmails, suggestion.email];
                          const uniqueToEmails = newToEmails.filter((email, index) => 
                            newToEmails.indexOf(email) === index
                          );
                          setToEmails(uniqueToEmails);
                          setEmails([...uniqueToEmails, ...ccEmails]);
                          setShowToSuggestions(false);
                          setCurrentToInput("");
                          setToInputKey(prev => prev + 1);
                        }}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: index < toEmailSuggestions.length - 1 ? "1px solid #eee" : "none",
                          display: "flex",
                          flexDirection: "column",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f0f8ff";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "white";
                        }}
                      >
                        <span style={{ fontWeight: "500", color: " #FF5E4F" }}>
                          {suggestion.name}
                        </span>
                        <span style={{ fontSize: "12px", color: "#666" }}>
                          {suggestion.email}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        padding: "12px",
                        color: "#dc3545",
                        fontStyle: "italic",
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "40px",
                        fontSize: "14px",
                        fontWeight: "500"
                      }}
                    >
                      Email does not exist
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* CC Field */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  fontSize: "14px",
                  color: "black",
                  fontWeight: "600",
                  marginLeft: "16px",
                  display: "block",
                  marginBottom: "8px"
                }}
              >
                Cc: PM, DM, DelEx
              </label>
              <ReactMultiEmail
                key={ccInputKey}
                placeholder={loading ? "Email ID fetching..." : "Add CC recipients"}
                emails={ccEmails}
                className="reactMultiEmail"
                style={{
                  minHeight: "30px",
                  maxHeight: "80px",
                  overflowY: "auto",
                  backgroundColor: "white",
                  scrollbarColor: " #FF5E4F white",
                  padding: "0.5rem",
                  borderRadius: "4px",
                  marginLeft: "16px",
                  width: "635px",
                  scrollbarWidth:'thin',
                  border:"1px solid  #FF5E4F",
                  color: "#074173"
                }}
                onChange={(newEmails: string[]) => {
                  const validEmails = newEmails.filter((email) =>
                    email.toLowerCase().endsWith("@ltimindtree.com")
                  );

                  if (validEmails.length !== newEmails.length) {
                    toast.error("Please use a valid Email Address");
                  }

                  setCcEmails(validEmails);
                  setEmails([...toEmails, ...validEmails]);
                  setShowCcSuggestions(false);
                }}
                onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  const target = e.target as HTMLInputElement;
                  const value = target.value.trim();
                  setCurrentCcInput(value);
                  if (value === '') {
                    setShowCcSuggestions(false);
                    setCcEmailSuggestions([]);
                  } else {
                    fetchEmailSuggestions(value, 'cc');
                  }
                }}
                onFocus={() => {
                  if (currentCcInput && currentCcInput.trim() !== '') {
                    fetchEmailSuggestions(currentCcInput, 'cc');
                  }
                }}
                getLabel={(email, index, removeEmail) => (
                  <div
                    key={index}
                    className={classes.emailTag}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: getEmailTypeColor(email),
                      border: "0px solid transparent",
                      borderRadius: "100px",
                      margin: "0.2rem",
                    }}
                  >
                    <div
                      style={{
                        marginRight: "0.5rem",
                        translate: "10px -1px",
                        color: "#3c3c3c",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start"
                      }}
                    >
                      <span>{email}</span>
                      {getEmailTypeLabel(email) && (
                        <span style={{ 
                          fontSize: "10px", 
                          color: "#666",
                          fontWeight: "bold"
                        }}>
                          {getEmailTypeLabel(email)}
                        </span>
                      )}
                    </div>
                    <IconButton
                      onClick={() => handleRemoveCcEmail(index)}
                      size="small"
                    >
                      <CloseIcon
                        style={{
                          color: "grey",
                          fontSize: "1.3rem",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "50%",
                          transition: "transform 0.2s, color 0.2s",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.color = " #FF5E4F";
                          e.currentTarget.style.transform = "scale(1.2)";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.color = "grey";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      />
                    </IconButton>
                  </div>
                )}
              />
              
              {showCcSuggestions && (
                <div
                  data-suggestion-dropdown="true"
                  style={{
                    position: "absolute",
                    bottom: "60px",
                    right: "-200px",
                    width: "300px",
                    backgroundColor: "white",
                    border: "1px solid #FF5E4F",
                    borderRadius: "4px",
                    maxHeight: "150px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                    scrollbarWidth:'thin',
                  }}
                > 
                  {ccEmailSuggestions.length > 0 ? (
                    ccEmailSuggestions.map((suggestion: any, index: number) => (
                      <div
                        key={index}
                        onClick={() => {
                          const newCcEmails = [...ccEmails, suggestion.email];
                          const uniqueCcEmails = newCcEmails.filter((email, index) => 
                            newCcEmails.indexOf(email) === index
                          );
                          setCcEmails(uniqueCcEmails);
                          setEmails([...toEmails, ...uniqueCcEmails]);
                          setShowCcSuggestions(false);
                          setCurrentCcInput("");
                          setCcInputKey(prev => prev + 1);
                        }}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: index < ccEmailSuggestions.length - 1 ? "1px solid #eee" : "none",
                          display: "flex",
                          flexDirection: "column",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f0f8ff";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "white";
                        }}
                      >
                        <span style={{ fontWeight: "500", color: " #FF5E4F" }}>
                          {suggestion.name}
                        </span>
                        <span style={{ fontSize: "12px", color: "#666" }}>
                          {suggestion.email}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        padding: "12px",
                        color: "#dc3545",
                        fontStyle: "italic",
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "40px",
                        fontSize: "14px",
                        fontWeight: "500"
                      }}
                    >
                      Email does not exist
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div
              className={classes.buttonEmail}
              style={{
                position: "absolute",
                bottom: "16px",
                left: "33px",
                display: "flex",
                justifyContent: "space-between",
                width: "600px",
                backgroundColor: "#f5f5f5",
                gap: "10px",
                flexWrap: "wrap"
              }}
            >
              <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  onClick={refreshPMDMEmails}
                  style={{
                    borderColor: "#4fa1ff",
                    color: "#4f98ff",
                    fontSize: "10px",
                    padding: "4px 8px",
                    minWidth: "auto"
                  }}
                >
                  Refresh PM/DM/PPH
                </Button>
                {excludedDefaultEmails.length > 0 && (
                  <Button
                    variant="outlined"
                    onClick={restoreDefaultEmails}
                    style={{
                      borderColor: "#2196f3",
                      color: "#2196f3",
                      fontSize: "10px",
                      padding: "4px 8px",
                      minWidth: "auto"
                    }}
                  >
                    Restore Default
                  </Button>
                )}
                {excludedPmDmEmails.length > 0 && (
                  <Button
                    variant="outlined"
                    onClick={restorePMDMEmails}
                    style={{
                      borderColor: "#ff9800",
                      color: "#ff9800",
                      fontSize: "10px",
                      padding: "4px 8px",
                      minWidth: "auto"
                    }}
                  >
                    Restore PM/DM/PPH
                  </Button>
                )}
              </div>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon style={{ fontWeight: "bold" }} />}
                onClick={handleSendEmail}
                style={{
                  background: " linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)"
                }}
              >
                Save & Send
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>


  );
};
export default SendNotification;